/*
 * PDP cross-sell — A/B test T3, variant B (html.pdp-xsell-b).
 *
 * Variant B always shows our block; the Essential Upsell control is never shown in B.
 * Recommendations are picked live from the catalog, falling back tier by tier:
 *   care (face / body / hair): ritual (brand first, other brands fill missing steps) → brand → category → store
 *   perfume:                   discover (brand discovery / travel / same scent) → similar aromas → category → store
 *   makeup:                    look (other makeup categories of the brand) → brand → category → store
 *   other:                     brand → store
 *
 * Data: /collections/vendors?q=<vendor>&view=xsell-data and /collections/all?view=xsell-data,
 * both sorted best-selling (templates/collection.xsell-data.liquid).
 *
 * Every line added from the block carries private properties for attribution (kept on the order,
 * hidden from the buyer): _alc_xsell, _alc_xsell_tier, _alc_xsell_role, _alc_xsell_from.
 */
(function () {
  const FLAG = 'pdp-xsell-b';
  const CACHE_MS = 30 * 60 * 1000;
  const ADDED_MS = 1200;
  const MIN_PRICE_CAP = 4000;
  const TEST_ID = 'T3-B';

  /* ====== Catalog rules ====== */

  const FACE_STEPS = [
    ['cleanse', /cleans|почиств|мицел|micellar|face wash|foam|пяна|make-?up remov|дегрим/],
    ['tone', /тоник|toner|tonic|essence|есенция|face mist|мист за лице|\bmist\b/],
    ['exfoliate', /exfoli|ексфол|peel|пилинг|scrub|скраб|enzyme|ензим/],
    ['eye', /\beye|околооч|за очи/],
    ['serum', /serum|серум|ampoule|ампул|face oil|олио за лице|масло за лице|treatment oil|booster|concentrate|drops|капки/],
    ['mask', /mask|masque|маска|patch/],
    ['spf', /spf|\bsun|слънц/],
    ['moisturize', /cream|крем|moistur|хидратиращ|emulsion|емулсия|balm|балсам|lotion|лосион/],
  ];
  const BODY_STEPS = [
    ['body_cleanse', /shower|душ|soap|сапун|wash|bath|ваната|пяна/],
    ['body_exfoliate', /scrub|скраб|exfol/],
    ['hands', /hand|ръце/],
    ['deo', /deodorant|дезодорант/],
    ['body_moisturize', /lotion|лосион|cream|крем|butter|масло|oil|олио|balm|балсам|milk|мляко|glaze|serum|серум/],
  ];
  const HAIR_STEPS = [
    ['shampoo', /shampoo|шампоан/],
    ['condition', /conditioner|балсам|кондиционер/],
    ['hair_mask', /mask|masque|маска/],
    ['style', /styl|wax|восък|hairspray|лак за коса|mousse|пяна за коса|blow dry|сешоар/],
    ['treat', /oil|олио|serum|серум|treatment|спрей|spray|leave/],
  ];
  const STEPS = {face: FACE_STEPS, body: BODY_STEPS, hair: HAIR_STEPS};
  // Order of the steps in a routine (STEPS above is the matching priority, not the routine order).
  const ROUTINE = {
    face: ['cleanse', 'tone', 'exfoliate', 'serum', 'eye', 'moisturize', 'mask', 'spf'],
    body: ['body_cleanse', 'body_exfoliate', 'body_moisturize', 'hands', 'deo'],
    hair: ['shampoo', 'condition', 'hair_mask', 'treat', 'style'],
  };
  const CORE = {
    face: ['cleanse', 'serum', 'moisturize', 'eye', 'tone', 'exfoliate', 'mask', 'spf'],
    body: ['body_cleanse', 'body_moisturize', 'body_exfoliate', 'hands', 'deo'],
    hair: ['shampoo', 'condition', 'hair_mask', 'treat', 'style'],
  };
  const MAKEUP_AREAS = [
    ['lips', /lip|устн|червило|гланц/],
    ['eyes', /eye|mascara|спирала|brow|вежд|liner|очна|сенки|shadow/],
    ['face', /foundation|фон дьо тен|concealer|коректор|blush|руж|bronz|бронз|contour|контур|highlight|хайлайт|powder|пудра|cheek/],
  ];

  const low = (value) => (value || '').toLowerCase();
  const tags = (p) => p.tg || [];
  const hasTag = (p, list) => tags(p).some((tag) => list.includes(tag));
  const price = (p) => (p.fv ? p.fv[2] : 0) || 0;
  const text = (p) => `${low(p.t)} ${low(p.ty)}`;
  const list = (value) => (Array.isArray(value) ? value : value ? [value] : []);

  const isGift = (p) => tags(p).includes('gift_product') || /подарък|gift card|ваучер/.test(low(p.t));
  const isSet = (p) =>
    /\b(set|kit|комплект|сет|coffret|discovery|duo|trio|collection|travel|mini|nomad|carton|box)\b/.test(text(p)) ||
    hasTag(p, ['mini', 'Мини', 'Комплект']);
  const isKids = (p) => hasTag(p, ['Подходящо за деца', 'За бебета']) || /kids|baby|бебе|деца/.test(low(p.t));
  const isDiscovery = (p) => /discovery|coffret|sampler|пробни|мостри|откривател/.test(text(p));

  function kind(p) {
    const ty = low(p.ty);
    const ti = low(p.t);
    const t = tags(p);
    const isFragrance = ty === 'аромати' || ty === 'парфюм' || ty.includes('парфюм') || t.includes('Парфюми');
    const isBodyFormat = /body|hand|soap|shower|lotion|cream|candle|свещ|detergent|mist|diffuser|дифузер|шампоан|shampoo/.test(ti);
    if (isFragrance && !isBodyFormat) return 'perfume';
    if (t.includes('Грим') || ty.includes('грим') || /lip|mascara|foundation|blush|bronz|concealer|eyeliner|brow|contour|highlight|pencil|palette|червило|спирала/.test(ti)) return 'makeup';
    if (t.includes('Аромати за дома') || /свещ|дом|candle|diffuser|дифузер|incense|инсенс|detergent/.test(`${ty} ${ti}`)) return 'home';
    if (t.includes('Коса') || ty.includes('коса') || /shampoo|conditioner|шампоан|балсам за коса|hair/.test(ti)) return 'hair';
    if (/body|тяло|ръце|hand|shower|душ|bath|ваната|deodorant|дезодорант|soap|сапун/.test(`${ty} ${ti}`) || t.includes('Грижа за тяло')) return 'body';
    if (hasTag(p, ['рутина за лице', 'Лице', 'За лице', 'Кожа']) || ty.includes('лице')) return 'face';
    return 'other';
  }

  // Name first, then type, then tags: types and tags are often wrong (e.g. a serum typed as "Крем за лице").
  function step(p, k) {
    const rules = STEPS[k];
    if (!rules) return null;
    for (const field of [p.t, p.ty, tags(p).join(' ')]) {
      for (const [name, rx] of rules) {
        if (rx.test(low(field))) return name;
      }
    }
    return null;
  }

  function makeupArea(p) {
    for (const [name, rx] of MAKEUP_AREAS) {
      if (rx.test(text(p))) return name;
    }
    return null;
  }

  const STOP_WORDS = ['the', 'a', 'and', 'de', 'di', 'la', 'le', 'body', 'hand', 'hair', 'face', 'for', 'за', 'с', 'и', 'ml'];
  function lineKey(p) {
    const head = low(p.t).split(/\s[-–]\s|\s\d/)[0];
    return (head.match(/[\wа-я]+/g) || []).filter((word) => !STOP_WORDS.includes(word)).slice(0, 2).join(' ');
  }

  function scent(p) {
    return low(p.t)
      .replace(/eau de parfum|eau de toilette|extrait de parfum|extrait|parfum|edp|edt|\d+\s?ml|refillable|travel size|-/g, '')
      .replace(/[^\wа-я ]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /* ====== Selection ====== */

  function pickRitual(current, k, brand, store) {
    const currentStep = step(current, k);
    const cap = Math.max(price(current) * 2, MIN_PRICE_CAP);
    const fits = (p) => kind(p) === k && !isSet(p) && isKids(p) === isKids(current) && price(p) <= cap;
    const line = lineKey(current);
    const sameLineFirst = (items) =>
      k === 'face' || !line ? items : [...items.filter((p) => lineKey(p) === line), ...items.filter((p) => lineKey(p) !== line)];

    const byStep = {};
    const collect = (items) => {
      sameLineFirst(items.filter(fits)).forEach((p) => {
        const s = step(p, k);
        if (s && s !== currentStep && !byStep[s]) byStep[s] = p;
      });
    };

    collect(brand);
    const brandSteps = CORE[k].filter((s) => byStep[s]);
    if (brandSteps.length < 3 && store) collect(store);

    // Brand steps take the slots first; other brands only fill what the brand does not have.
    const picked = [...brandSteps, ...CORE[k].filter((s) => byStep[s] && !brandSteps.includes(s))].slice(0, 3);
    const order = ROUTINE[k];
    picked.sort((a, b) => order.indexOf(a) - order.indexOf(b));

    return {currentStep, items: picked.map((s) => ({p: byStep[s], step: s}))};
  }

  function pickDiscover(current, brand) {
    const name = scent(current);
    const sameScent = (p) => name.length > 3 && low(p.t).includes(name);
    const discovery = brand.filter(isDiscovery);
    const travel = brand.filter((p) => sameScent(p) && /travel|\b(5|7|7\.5|8|10|15) ?ml|mini/.test(low(p.t)));
    const layering = brand.filter((p) => sameScent(p) && ['body', 'hair', 'home'].includes(kind(p)));
    const out = [];
    [...discovery.slice(0, 1), ...travel.slice(0, 1), ...layering.slice(0, 1), ...discovery.slice(1, 2)].forEach((p) => {
      if (!out.includes(p)) out.push(p);
    });
    return out.slice(0, 2);
  }

  function pickSimilarAromas(current, store) {
    const aromas = list(current.ar);
    if (!aromas.length) return [];
    const seasons = list(current.se);
    const name = scent(current);
    const cap = Math.max(price(current) * 2, MIN_PRICE_CAP);

    return store
      .filter((p) => kind(p) === 'perfume' && !isDiscovery(p) && price(p) <= cap && !(name.length > 3 && low(p.t).includes(name)))
      .map((p, rank) => {
        const shared = list(p.ar).filter((a) => aromas.includes(a)).length;
        const season = list(p.se).filter((s) => seasons.includes(s)).length;
        return {p, score: shared * 2 + Math.min(season, 1), shared, rank};
      })
      .filter((entry) => entry.shared > 0)
      .sort((a, b) => b.score - a.score || a.rank - b.rank)
      .slice(0, 2)
      .map((entry) => entry.p);
  }

  function pickLook(current, brand) {
    const area = makeupArea(current);
    const out = [];
    const areas = new Set([area]);
    brand
      .filter((p) => kind(p) === 'makeup' && !isSet(p))
      .forEach((p) => {
        const a = makeupArea(p);
        if (a && !areas.has(a) && out.length < 2) {
          areas.add(a);
          out.push(p);
        }
      });
    return out;
  }

  const sameKind = (current, items) => items.filter((p) => kind(p) === kind(current));
  const priced = (current, items) => items.filter((p) => price(p) <= Math.max(price(current) * 2, MIN_PRICE_CAP));

  async function select(current, loadBrand, loadStore) {
    const k = kind(current);
    const brand = await loadBrand();
    let store = null;
    const getStore = async () => (store = store || (await loadStore()));

    if (STEPS[k]) {
      let ritual = pickRitual(current, k, brand, null);
      if (ritual.items.length < 3) ritual = pickRitual(current, k, brand, await getStore());
      if (ritual.items.length >= 2) {
        const mixed = ritual.items.some((item) => item.p.v !== current.v);
        return {tier: 'ritual', mode: 'ritual', items: ritual.items, currentStep: ritual.currentStep, kind: k, mixed};
      }
    }

    if (k === 'perfume') {
      const discover = pickDiscover(current, brand);
      if (discover.length) return {tier: 'discover', mode: 'discover', items: discover.map((p) => ({p}))};

      const similar = pickSimilarAromas(current, await getStore());
      if (similar.length) return {tier: 'similar', mode: 'discover', items: similar.map((p) => ({p}))};
    }

    if (k === 'makeup') {
      const look = pickLook(current, brand);
      if (look.length) return {tier: 'look', mode: 'discover', items: look.map((p) => ({p}))};
    }

    const brandBest = priced(current, sameKind(current, brand)).slice(0, 2);
    if (brandBest.length) return {tier: 'brand', mode: 'discover', items: brandBest.map((p) => ({p}))};

    const categoryBest = priced(current, sameKind(current, await getStore())).slice(0, 2);
    if (categoryBest.length) return {tier: 'category', mode: 'discover', items: categoryBest.map((p) => ({p}))};

    const storeBest = (await getStore()).slice(0, 2);
    return {tier: 'store', mode: 'discover', items: storeBest.map((p) => ({p}))};
  }

  /* ====== Data ====== */

  async function fetchList(url) {
    const key = `xsell:${url}`;
    try {
      const cached = JSON.parse(sessionStorage.getItem(key) || 'null');
      if (cached && Date.now() - cached.t < CACHE_MS) return cached.d;
    } catch (e) {}

    const response = await fetch(url, {credentials: 'same-origin'});
    if (!response.ok) throw new Error(`xsell data ${response.status}`);
    const data = JSON.parse(await response.text());

    try {
      sessionStorage.setItem(key, JSON.stringify({t: Date.now(), d: data}));
    } catch (e) {}
    return data;
  }

  async function cartProductIds() {
    try {
      const cart = await fetch(`${window.theme.routes.cart_url}.js`).then((r) => r.json());
      return new Set(cart.items.map((item) => item.product_id));
    } catch (e) {
      return new Set();
    }
  }

  /* ====== Rendering ====== */

  function formatMoney(cents) {
    if (window.themeCurrency && typeof window.themeCurrency.formatMoney === 'function') {
      return window.themeCurrency.formatMoney(cents, window.theme.moneyFormat);
    }
    const [whole, fraction] = (cents / 100).toFixed(2).split('.');
    return (window.theme.moneyFormat || '{{amount_with_comma_separator}} €').replace(
      /\{\{\s*\w+\s*\}\}/,
      `${whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${fraction}`,
    );
  }

  const escapeHtml = (value) =>
    String(value ?? '').replace(/[&<>"']/g, (c) => ({'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'})[c]);
  const fill = (template, values) => template.replace(/\[(\w+)\]/g, (match, key) => (key in values ? values[key] : match));
  const sizeLabel = (title) => (title && title !== 'Default Title' ? title : '');
  const CHECK_SVG =
    '<svg viewBox="0 0 12 12" width="12" height="12" aria-hidden="true" focusable="false"><path d="M2 6.5 4.8 9 10 3" fill="none" stroke="#fff" stroke-width="1.8" stroke-linecap="square"/></svg>';

  function thumb(p) {
    return `<span class="cross-sell__thumb">${p.img ? `<img src="${escapeHtml(p.img)}" alt="" loading="lazy" width="80" height="80">` : ''}</span>`;
  }

  function ritualRow(p, stepName, index, isCurrent, variant, strings, showVendor) {
    const label = fill(strings.ritual.step_aria, {step_number: index, step_name: stepName, product: p.t, price: formatMoney(variant[2])});
    const sizeHtml =
      !isCurrent && p.vs.length > 1
        ? `<span class="cross-sell__size">${escapeHtml(strings.ritual.size)}
            <span class="cross-sell__select">
              <select data-cross-sell-variant aria-label="${escapeHtml(fill(strings.ritual.size_aria, {product: p.t}))}">
                ${p.vs.map((v) => `<option value="${v[0]}" data-price="${v[2]}"${v[0] === variant[0] ? ' selected' : ''}>${escapeHtml(v[1])}</option>`).join('')}
              </select>
              <svg class="icon" viewBox="0 0 10 6" aria-hidden="true" focusable="false"><path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.2"/></svg>
            </span>
          </span>`
        : `<span class="cross-sell__size">${escapeHtml(sizeLabel(variant[1]))}</span>`;

    return `
      <label class="cross-sell__row${isCurrent ? ' is-current' : ''}">
        <input type="checkbox" class="cross-sell__check visually-hidden" value="${variant[0]}" data-price="${variant[2]}"
          data-product-id="${p.id}" ${isCurrent ? 'checked disabled data-current' : 'checked'}
          aria-label="${escapeHtml(isCurrent ? fill(strings.ritual.current_aria, {product: p.t}) : label)}">
        <span class="cross-sell__box" aria-hidden="true">${CHECK_SVG}</span>
        ${thumb(p)}
        <span class="cross-sell__text">
          <span class="cross-sell__meta">
            <span class="cross-sell__step">${escapeHtml(stepName ? `${index} · ${stepName}` : `${index}`)}</span>
            ${isCurrent ? `<span class="cross-sell__tag">${escapeHtml(strings.ritual.current)}</span>` : ''}
          </span>
          <span class="cross-sell__name">${escapeHtml(p.t)}</span>
          ${showVendor ? `<span class="cross-sell__vendor">${escapeHtml(p.v)}</span>` : ''}
          ${sizeHtml}
        </span>
        <span class="cross-sell__price" data-cross-sell-line-price>${formatMoney(variant[2])}</span>
      </label>`;
  }

  function discoverRow(p, label, strings) {
    const variant = p.fv;
    return `
      <div class="cross-sell__row cross-sell__row--discover">
        <a class="cross-sell__thumb-link" href="${escapeHtml(p.u)}" tabindex="-1" aria-hidden="true">${thumb(p)}</a>
        <span class="cross-sell__text">
          <span class="cross-sell__step">${escapeHtml(label)}</span>
          <a class="cross-sell__name" href="${escapeHtml(p.u)}">${escapeHtml(p.t)}</a>
          ${sizeLabel(variant[1]) ? `<span class="cross-sell__size">${escapeHtml(variant[1])}</span>` : ''}
          <span class="cross-sell__price-inline">${formatMoney(variant[2])}</span>
        </span>
        <button type="button" class="cross-sell__add" data-cross-sell-add-one="${variant[0]}" data-product-id="${p.id}"
          aria-label="${escapeHtml(fill(strings.discover.add_aria, {product: p.t, price: formatMoney(variant[2])}))}">${escapeHtml(strings.discover.add)}</button>
      </div>`;
  }

  function headings(selection, current, strings) {
    const brand = current.v;
    switch (selection.tier) {
      case 'ritual':
        return [strings.ritual.title, selection.mixed ? strings.ritual.subtitle_mixed : fill(strings.ritual.subtitle, {brand})];
      case 'discover':
        return [fill(strings.discover.title, {brand}), strings.discover.subtitle];
      case 'similar':
        return [strings.similar.title, fill(strings.similar.subtitle, {aromas: list(current.ar).slice(0, 3).join(', ')})];
      case 'look':
        return [strings.look.title, fill(strings.look.subtitle, {brand})];
      case 'brand':
        return [fill(strings.brand.title, {brand}), strings.brand.subtitle];
      default:
        return [strings.popular.title, strings.popular.subtitle];
    }
  }

  function render(root, selection, current, currentVariant, strings) {
    const [title, subtitle] = headings(selection, current, strings);
    const titleId = `xs-title-${current.id}`;
    let body;

    if (selection.mode === 'ritual') {
      const order = ROUTINE[selection.kind];
      const rows = selection.items.map((item) => ({p: item.p, step: item.step, current: false}));
      const currentIndex = selection.currentStep
        ? rows.filter((row) => order.indexOf(row.step) < order.indexOf(selection.currentStep)).length
        : 0;
      rows.splice(currentIndex, 0, {p: current, step: selection.currentStep, current: true});

      body = `
        <div class="cross-sell__list" role="group" aria-labelledby="${titleId}">
          ${rows
            .map((row, i) =>
              ritualRow(
                row.p,
                row.step ? strings.steps[row.step] : '',
                i + 1,
                row.current,
                row.current ? currentVariant : row.p.fv,
                strings,
                selection.mixed && !row.current,
              ),
            )
            .join('')}
        </div>
        <button type="button" class="btn cross-sell__cta" data-cross-sell-add data-state="idle"></button>`;
    } else {
      body = `<div class="cross-sell__list">${selection.items.map((item) => discoverRow(item.p, item.p.v, strings)).join('')}</div>`;
    }

    root.querySelector('[data-cross-sell-content]').innerHTML = `
      <section class="cross-sell__inner" aria-labelledby="${titleId}" data-mode="${selection.mode}" data-tier="${selection.tier}">
        <h2 class="cross-sell__title" id="${titleId}">${escapeHtml(title)}</h2>
        <p class="cross-sell__sub">${escapeHtml(subtitle)}</p>
        ${body}
        <p class="visually-hidden" aria-live="polite" data-cross-sell-live></p>
      </section>`;
  }

  /* ====== Behaviour ====== */

  // Same event names in both groups so Intelligems can compare A and B on one metric.
  // Sent to GTM (dataLayer) and to Intelligems (igEvents: custom metric or results filter).
  const group = () => (document.documentElement.classList.contains(FLAG) ? 'B' : 'A');
  function pushEvent(event, data) {
    const properties = {xsell_group: group(), ...data};
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({event, xsell_test: 'T3', ...properties});
    window.igEvents = window.igEvents || [];
    window.igEvents.push({event, properties});
  }

  // One view per page, when the recommendations actually scroll into view.
  function trackView(element, data) {
    if (element.dataset.xsellViewTracked) return;
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      observer.disconnect();
      if (element.dataset.xsellViewTracked) return;
      element.dataset.xsellViewTracked = 'true';
      pushEvent('xsell_view', data());
    });
    observer.observe(element);
  }

  function init(root) {
    if (root.dataset.crossSellInit) return;
    root.dataset.crossSellInit = 'true';

    let current;
    let strings;
    try {
      current = JSON.parse(root.querySelector('[data-cross-sell-product]').textContent);
      strings = JSON.parse(root.querySelector('[data-cross-sell-strings]').textContent);
    } catch (e) {
      root.hidden = true;
      return;
    }

    const section = root.closest('section, .shopify-section') || document.body;
    const form = root.querySelector('[data-cross-sell-form]');
    const inputs = root.querySelector('[data-cross-sell-inputs]');
    const submit = root.querySelector('[data-cross-sell-submit]');
    const errors = root.querySelector('[data-cart-errors-container]');
    let currentVariant = current.fv;
    let selection = null;
    let pending = null;

    const live = () => root.querySelector('[data-cross-sell-live]');
    const cta = () => root.querySelector('[data-cross-sell-add]');

    function updateCta() {
      const button = cta();
      if (!button || button.dataset.state !== 'idle') return;
      const checks = [...root.querySelectorAll('.cross-sell__check')];
      const picked = checks.filter((c) => c.checked);
      const recs = picked.filter((c) => !c.hasAttribute('data-current')).length;
      const total = picked.reduce((sum, c) => sum + Number(c.dataset.price), 0);

      if (recs === 0) {
        button.setAttribute('aria-disabled', 'true');
        button.textContent = strings.ritual.add_disabled;
        return;
      }

      button.setAttribute('aria-disabled', 'false');
      button.textContent = fill(strings.ritual.add, {count: picked.length, total: formatMoney(total)});
      live().textContent = fill(strings.ritual.add_aria, {count: picked.length, total: formatMoney(total)});
    }

    function addToCart(lines, button) {
      inputs.innerHTML = lines
        .map((line, i) => {
          const props = {
            _alc_xsell: TEST_ID,
            _alc_xsell_tier: selection.tier,
            _alc_xsell_role: line.role,
            _alc_xsell_from: current.h,
          };
          return [
            `<input type="hidden" name="items[${i}][id]" value="${line.id}">`,
            `<input type="hidden" name="items[${i}][quantity]" value="1">`,
            ...Object.entries(props).map(
              ([key, value]) => `<input type="hidden" name="items[${i}][properties][${key}]" value="${escapeHtml(value)}">`,
            ),
          ].join('');
        })
        .join('');

      pending = {button, lines};
      errors.innerHTML = '';
      button.dataset.state = 'loading';
      button.textContent = strings.adding;

      // The theme's own add-to-cart flow handles the drawer, the T1 gift logic and stock errors.
      submit.removeAttribute('disabled');
      submit.click();

      pushEvent('xsell_add', {
        xsell_tier: selection.tier,
        xsell_items: lines.filter((line) => line.role === 'rec').length,
        xsell_value: lines.filter((line) => line.role === 'rec').reduce((sum, line) => sum + line.price, 0) / 100,
        xsell_from: current.h,
      });
    }

    function finish(success) {
      if (!pending) return;
      const {button, lines} = pending;
      pending = null;

      if (!success) {
        button.dataset.state = 'idle';
        restore(button);
        return;
      }

      button.dataset.state = 'added';
      button.classList.add('is-added');
      button.textContent = strings.added;
      live().textContent = fill(strings.added_aria, {count: lines.length});
      setTimeout(() => {
        button.dataset.state = 'idle';
        button.classList.remove('is-added');
        restore(button);
      }, ADDED_MS);
    }

    function restore(button) {
      if (button.hasAttribute('data-cross-sell-add')) updateCta();
      else button.textContent = strings.discover.add;
    }

    root.addEventListener('change', (event) => {
      const select = event.target.closest('[data-cross-sell-variant]');
      if (select) {
        const option = select.selectedOptions[0];
        const row = select.closest('.cross-sell__row');
        const check = row.querySelector('.cross-sell__check');
        check.value = option.value;
        check.dataset.price = option.dataset.price;
        row.querySelector('[data-cross-sell-line-price]').textContent = formatMoney(Number(option.dataset.price));
      }
      if (select || event.target.matches('.cross-sell__check')) updateCta();
    });

    root.addEventListener('click', (event) => {
      const bundle = event.target.closest('[data-cross-sell-add]');
      if (bundle) {
        if (bundle.getAttribute('aria-disabled') === 'true' || bundle.dataset.state !== 'idle') return;
        const lines = [...root.querySelectorAll('.cross-sell__check')]
          .filter((c) => c.checked)
          .map((c) => ({id: c.value, price: Number(c.dataset.price), role: c.hasAttribute('data-current') ? 'anchor' : 'rec'}));
        addToCart(lines, bundle);
        return;
      }

      const single = event.target.closest('[data-cross-sell-add-one]');
      if (single) {
        if (single.dataset.state && single.dataset.state !== 'idle') return;
        const item = selection.items.find((entry) => String(entry.p.id) === single.dataset.productId);
        addToCart([{id: single.dataset.crossSellAddOne, price: item ? price(item.p) : 0, role: 'rec'}], single);
      }
    });

    document.addEventListener('theme:cart:added', () => finish(true));
    new MutationObserver(() => {
      if (pending && errors.textContent.trim()) finish(false);
    }).observe(errors, {childList: true, subtree: true});

    document.addEventListener('theme:variant:change', (event) => {
      const variant = event.detail && event.detail.variant;
      if (!variant || !(event.target instanceof Node) || !event.target.contains(root)) return;
      currentVariant = [variant.id, variant.title, variant.price];
      const check = root.querySelector('.cross-sell__check[data-current]');
      if (!check) return;
      check.value = variant.id;
      check.dataset.price = variant.price;
      const row = check.closest('.cross-sell__row');
      row.querySelector('[data-cross-sell-line-price]').textContent = formatMoney(variant.price);
      const size = row.querySelector('.cross-sell__size');
      if (size) size.textContent = sizeLabel(variant.title);
      updateCta();
    });

    (async () => {
      try {
        const inCart = await cartProductIds();
        const usable = (items) =>
          items.filter((p) => p.id !== current.id && p.a && p.vs && p.vs.length && !isGift(p) && !inCart.has(p.id));
        selection = await select(
          current,
          async () => usable(await fetchList(root.dataset.brandUrl)),
          async () => usable(await fetchList(root.dataset.storeUrl)),
        );

        if (!selection.items.length) throw new Error('xsell: nothing to show');

        const variantId = Number(new URLSearchParams(window.location.search).get('variant'));
        const fromUrl = current.vs.find((v) => v[0] === variantId);
        if (fromUrl) currentVariant = fromUrl;

        render(root, selection, current, currentVariant, strings);
        root.removeAttribute('aria-busy');
        root.classList.add('is-ready');
        updateCta();

        trackView(root, () => ({
          xsell_tier: selection.tier,
          xsell_items: selection.items.length,
          xsell_from: current.h,
          xsell_mixed: Boolean(selection.mixed),
        }));
      } catch (error) {
        // No control in variant B: if even the store-wide fallback fails, the block simply disappears.
        root.hidden = true;
        pushEvent('xsell_error', {xsell_from: current.h, xsell_message: String(error && error.message)});
      }
    })();
  }

  function initAll() {
    if (!document.documentElement.classList.contains(FLAG)) return;
    document.querySelectorAll('[data-cross-sell]').forEach(init);
  }

  /* ====== Control group (A): the same events for the Essential Upsell blocks ====== */

  const CONTROL_SELECTOR = '.essential-upsell-frequently-bought-together-block, .essential-upsell-cross-sell-block';
  const controlWidget = (element) => (element.classList.contains('essential-upsell-cross-sell-block') ? 'cross_sell' : 'fbt');
  const productHandle = () => (window.location.pathname.match(/\/products\/([^/?#]+)/) || [])[1] || '';

  function trackControlViews() {
    if (group() !== 'A') return;
    document.querySelectorAll(CONTROL_SELECTOR).forEach((widget) => {
      // The app fills the widget after load; count it only once it has content.
      if (!widget.textContent.trim()) return;
      trackView(widget, () => ({xsell_tier: 'control', xsell_widget: controlWidget(widget), xsell_from: productHandle()}));
    });
  }

  if (document.querySelector(CONTROL_SELECTOR)) {
    new MutationObserver(trackControlViews).observe(document.body, {childList: true, subtree: true});
    trackControlViews();
  }

  document.addEventListener(
    'click',
    (event) => {
      if (group() !== 'A') return;
      const widget = event.target.closest(CONTROL_SELECTOR);
      const button = event.target.closest('button');
      if (!widget || !button || !/добав|купет|add/i.test(button.textContent)) return;
      pushEvent('xsell_add', {xsell_tier: 'control', xsell_widget: controlWidget(widget), xsell_from: productHandle()});
    },
    true,
  );

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();
  document.addEventListener('shopify:section:load', initAll);
  new MutationObserver(initAll).observe(document.documentElement, {attributes: true, attributeFilter: ['class']});
})();
